import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
//import OrganizationService from './OrganizationService';
import ApiService from '../Components/apiService'; // Import ApiService class
import { BASE_URL, DateFormat } from '../Components/Constant';
import {
  Layout,
  Card,
  Typography,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Space,
  Badge,
  Tooltip,
  Avatar,
  Progress,
} from 'antd';
import {
  PlusOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
  DatabaseOutlined,
  SettingOutlined,
  TeamOutlined,
  SafetyOutlined,
} from '@ant-design/icons';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState('loading'); 

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      console.log('Dashboard: Fetching organizations from API...');
      
      const apiService = new ApiService(
          `${BASE_URL}/super_admin/organizations`,
          { 'Content-Type': 'application/json' },
          'GET'
        );
    const apiData = await apiService.makeCall();
      console.log(' Dashboard: API Response:', apiData);
      
      if (apiData && apiData.organizations && apiData.organizations.length > 0) {
        console.log(' Dashboard: Using API data');
        //console.log('new',apiData.organizations[0].id);
        // Map API data to expected format
        const mappedOrganizations = apiData.organizations.map(org => ({
          id: org.id,
          name: org.name,
          domain: org.instance_url ? new URL(org.instance_url).hostname : 'N/A',
          plan: org.category, // Default since API doesn't provide this
          status: org.status === 'active' ? 'Active' : 'Inactive',
          createdAt: org.created_at,
          users: {
            total: org.user_count || 0,
            active: org.user_count || 0,
            inactive: 0,
            admins: 1,
          },
          objects: {
            total: org.object_count || 0,
            custom: org.object_count || 0,
            standard: 0,
          },
          sandbox_count: {
            total: org.sandbox_count || 0,
            active: org.sandbox_count || 0,
            inactive  : 0,
          },
          storage: {
            used: 10,
            limit: 50,
            percentage: 20,
          },
          apiCalls: {
            daily: 1000,
            monthly: 30000,
            limit: 100000,
          },
          features: {
            customObjects: true,
            apiAccess: true,
            advancedReporting: true,
            sso: false,
          },
          lastActivity: org.created_at,
        }));
        
        setOrganizations(mappedOrganizations);
        setDataSource('api');
        
       
      } else {
        setDataSource('fallback');
      }
    } catch (err) {
         const errorMessage = err && typeof err === 'object'
        ? Object.entries(err).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
        : 'Failed to fetch due to an unknown error';
      if(errorMessage==='error: Invalid or missing super admin token'){
        navigate('/Admin/Authentication');
      }
      message.error(errorMessage);
      setDataSource('fallback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleAddOrganization = (values) => {
    const newOrg = {
      id: Date.now(),
      name: values.name,
      domain: values.domain,
      plan: values.plan,
      status: 'Active',
      createdAt: new Date().toISOString(),
      users: {
        total: 0,
        active: 0,
        inactive: 0,
        admins: 1,
      },
      objects: {
        total: 0,
        custom: 0,
        standard: 0,
      },
      sandbox_count: {
        total: 0,
        active: 0,
        inactive: 0,
      },
      storage: {
        used: 0,
        limit: values.plan === 'Enterprise' ? 100 : values.plan === 'Professional' ? 50 : 10,
        percentage: 0,
      },
      apiCalls: {
        daily: 0,
        monthly: 0,
        limit: values.plan === 'Enterprise' ? 1000000 : values.plan === 'Professional' ? 500000 : 100000,
      },
      features: {
        customObjects: values.plan !== 'Basic',
        apiAccess: true,
        advancedReporting: values.plan === 'Enterprise',
        sso: values.plan === 'Enterprise',
      },
      lastActivity: new Date().toISOString(),
    };

    setOrganizations([...organizations, newOrg]);
    setIsAddModalVisible(false);
    form.resetFields();
    message.success('Organization added successfully!');
  };

  const handleDeleteOrganization = (orgId) => {
    setOrganizations(organizations.filter(org => org.id !== orgId));
    message.success('Organization deleted successfully!');
  };

  const handleViewDetails = (org) => {
    console.log('Viewing details for organization:', org);
    navigate(`/Admin/organization/${org.id}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Inactive': return 'default';
      case 'Suspended': return 'error';
      default: return 'default';
    }
  };

  const getPlanColor = (plan) => {
    switch (plan) {
      case 'Enterprise': return 'purple';
      case 'Professional': return 'blue';
      case 'Basic': return 'green';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'Organization',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space
          style={{ cursor: "pointer" }}
          onClick={() => handleViewDetails(record)} // 👈 navigate on click
        >
          <Avatar style={{ backgroundColor: '#1890ff' }}>
            {text.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, color: "#1677ff" }}>
              {text}
            </div>
            {/* <Text type="secondary" style={{ fontSize: '12px' }}>{record.domain}</Text> */}
          </div>
        </Space>
      ),
    },
    
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan) => <Tag color={getPlanColor(plan)}>{plan}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <Badge status={getStatusColor(status)} text={status} />,
    },
    {
      title: 'Users',
      dataIndex: ['users', 'total'],
      key: 'users',
      render: (total, record) => (
        <Tooltip title={`Active: ${record.users.active}, Inactive: ${record.users.inactive}`}>
          <Badge count={record.users.active} style={{ backgroundColor: '#52c41a' }}>
            <TeamOutlined style={{ fontSize: 16 }} />
          </Badge>
          <span style={{ marginLeft: 8 }}>{total}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Objects',
      dataIndex: ['objects', 'total'],
      key: 'objects'
    },
    {
      title: 'Sandbox Count',
      dataIndex: ['sandbox_count', 'total'],
      key: 'sandbox_count',
      // render: (total, record) => (
      //   // <Tooltip title={`Custom: ${record.objects.custom}, Standard: ${record.objects.standard}`}>
      //   //   <DatabaseOutlined style={{ marginRight: 4 }} />
      //   //   {total}
      //   // </Tooltip>
      // ),
    },
    {
      title: 'Storage',
      dataIndex: 'storage',
      key: 'storage',
      render: (storage) => (
        <div style={{ width: 100 }}>
          <Progress
            percent={storage.percentage}
            size="small"
            format={() => `${storage.used}/${storage.limit}GB`}
          />
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {/* <Tooltip title="Delete Organization">
            <Popconfirm
              title="Are you sure you want to delete this organization?"
              description="This action cannot be undone."
              onConfirm={() => handleDeleteOrganization(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="Clone Organization">
            <Button
              icon={<CopyOutlined />}
              size="small"
              onClick={() => handleCloneOrganization(record.id)}
            />
          </Tooltip> */
          }
        </Space>
      ),
    },
  ];

  const totalStats = {
    totalOrgs: organizations.length,
    activeOrgs: organizations.filter(org => org.status === 'Active').length,
    totalUsers: organizations.reduce((sum, org) => sum + org.users.total, 0),
    totalObjects: organizations.reduce((sum, org) => sum + org.objects.total, 0),
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#fff', padding: '0 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
              <SettingOutlined style={{ marginRight: 8 }} />
              Super Admin Dashboard
            </Title>
            <div style={{ marginTop: 8 }}>
              {/* <Text type="secondary" style={{ fontSize: '14px', marginRight: 8 }}>
                Data Source:
              </Text> */}
              {/* <Tag 
                color={dataSource === 'api' ? 'green' : dataSource === 'fallback' ? 'orange' : 'blue'}
                style={{ fontSize: '12px' }}
              >
                {dataSource === 'api' ? '✅ API Data' : dataSource === 'fallback' ? '⚠️ Mock Data (Fallback)' : '⏳ Loading...'}
              </Tag> */}
            </div>
          </Col>
          <Col>
            <Space>
              
              <Button
                icon={<SettingOutlined />}
                onClick={() => {
                  setDataSource('loading');
                  fetchOrganizations();
                }}
                loading={loading}
                size="large"
              >
                Refresh Data
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsAddModalVisible(true)}
                size="large"
              >
                Add Organization
              </Button>
            </Space>
          </Col>
        </Row>
      </Header>

      <Layout>
        <Content style={{ margin: '24px', background: '#f5f5f5' }}>
          {/* Statistics Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Organizations"
                  value={totalStats.totalOrgs}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Active Organizations"
                  value={totalStats.activeOrgs}
                  prefix={<SafetyOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Users"
                  value={totalStats.totalUsers}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Objects"
                  value={totalStats.totalObjects}
                  prefix={<DatabaseOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Organizations Table */}
          <Card title="Organizations" extra={<Text type="secondary">{organizations.length} organizations</Text>}>
            <Table
              columns={columns}
              dataSource={organizations}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                // showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </Card>

          {/* Add Organization Modal */}
          <Modal
            title="Add New Organization"
            open={isAddModalVisible}
            zIndex={1002}   // higher than sidebar
            onCancel={() => {
              setIsAddModalVisible(false);
              form.resetFields();
            }}
            footer={null}
            width={600}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleAddOrganization}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="Organization Name"
                    rules={[{ required: true, message: 'Please enter organization name' }]}
                  >
                    <Input placeholder="Enter organization name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="domain"
                    label="Domain"
                    rules={[
                      { required: true, message: 'Please enter domain' },
                      { pattern: /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/, message: 'Please enter a valid domain' }
                    ]}
                  >
                    <Input placeholder="example.com" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="plan"
                label="Plan"
                rules={[{ required: true, message: 'Please select a plan' }]}
              >
                <Select placeholder="Select a plan">
                  <Option value="Basic">Basic</Option>
                  <Option value="Professional">Professional</Option>
                  <Option value="Enterprise">Enterprise</Option>
                </Select>
              </Form.Item>
              <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                <Space>
                  <Button onClick={() => {
                    setIsAddModalVisible(false);
                    form.resetFields();
                  }}>
                    Cancel
                  </Button>
                  <Button type="primary" htmlType="submit">
                    Add Organization
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>

          {/* Organization Details Modal */}
          {/* This modal is no longer needed as navigation handles details */}
        </Content>
      </Layout>

      {/* Test OrganizationService Component */}
      {/* This component is no longer imported or used */}
    </Layout>
  );
};

export default SuperAdminDashboard;